/* eslint-disable no-use-before-define */
/* eslint-disable no-irregular-whitespace */
/* eslint-disable no-console */
import React, { FC, useEffect, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import { store } from 'redux/store';
import { State } from 'redux/initialState';

import { Partner, User } from 'types';

import presetRecipients from 'constants/presetRecipients';
import { MAX_PARTNERS_THRESHOLD } from 'constants/defaultConstants';

import { CustomAsyncSelectOption } from 'modules/_shared/components/CustomAsyncSelect/types/CustomAsyncSelectOption';
import { addAlert } from 'modules/_shared/redux/alertActions';
import { getUsers } from 'modules/profile/redux/userActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import { sendCommEmail } from 'modules/communications/redux/communicationActions';
import { useGetPartners } from 'modules/partners/hooks/useGetPartners';

import errorSwal from 'utils/errorSwal';
import { isPlatformAXA } from 'utils/showDetails';
import { getSocietyEmails } from 'utils/filters';
import { isValidEmailBody, isValidEmailSubject } from 'utils/cleanText';

import '../Modals.scss';
import SendEmailHeader from './components/SendEmailHeader';
import SendEmailRecipientsMenu from './components/SendEmailRecipientsMenu';
import SendEmailAddresses from './components/SendEmailAddresses';
import SendEmailSubject from './components/SendEmailSubject';
import SendEmailBody from './components/SendEmailBody';
import SendEmailFooter from './components/SendEmailFooter';
import SendEmailCustomRecipientsMenu from './components/SendEmailCustomRecipientsMenu';
import CustomLoading from '../../CustomLoading';

type SendEmailProps = {
  defaultRecipient?: Partner;
};

type RecipientEmail = {
  name: string;
  email: string;
};

const SendEmail: FC<SendEmailProps> = ({ defaultRecipient }) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const actualSociety = useSelector(
    (state: State) => state.society.actualSociety
  );
  const partners = useSelector((state: State) => state.partners);

  const hasDefaultRecipient = Boolean(defaultRecipient);

  const [isSending, setIsSending] = useState<boolean>(false);

  const [recipients, setRecipients] = useState<
    Record<string, RecipientEmail[]>
  >({
    PARTNERS: [],
    BENEFICIARIES: [],
    INVESTORS: [],
    DIRECTORS: [],
  });

  const [to, setTo] = useState<string | RecipientEmail[]>(
    defaultRecipient
      ? [{ name: defaultRecipient.name, email: defaultRecipient.email }]
      : []
  );
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [presetGroup, setPresetGroup] = useState<string>('');
  const [secondaryEmails, setSecondaryEmails] = useState<RecipientEmail[]>([]);

  const [customPartnerOption, setCustomPartnerOption] =
    useState<CustomAsyncSelectOption | null>(null);
  const [customCategoryOption, setCustomCategoryOption] =
    useState<CustomAsyncSelectOption | null>(null);

  useGetPartners({
    societyId: actualSociety?._id,
    items: MAX_PARTNERS_THRESHOLD,
  });

  const storeUniqueRecipients = (
    fromArray: RecipientEmail[],
    toArray: RecipientEmail[],
    uniqueEmails: string[]
  ) => {
    fromArray.forEach((element) => {
      if (!uniqueEmails.includes(element.email)) {
        toArray.push(element);
        uniqueEmails.push(element.email);
      }
    });
  };

  const handleSetRecipients = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedGroup = event.target.value;
    const uniqueRecipients: RecipientEmail[] = [];
    const uniqueEmails: string[] = [];

    setPresetGroup(selectedGroup);

    switch (selectedGroup) {
      // Custom case
      case presetRecipients.CUSTOM: {
        setTo([]);
        break;
      }
      // Partners case
      case presetRecipients.PARTNERS: {
        storeUniqueRecipients(
          recipients.PARTNERS,
          uniqueRecipients,
          uniqueEmails
        );
        storeUniqueRecipients(secondaryEmails, uniqueRecipients, uniqueEmails);
        setTo(uniqueRecipients);
        break;
      }
      // Beneficiaries case
      case presetRecipients.BENEFICIARIES: {
        storeUniqueRecipients(
          recipients.BENEFICIARIES,
          uniqueRecipients,
          uniqueEmails
        );
        setTo(uniqueRecipients);
        break;
      }
      // Investors case
      case presetRecipients.INVESTORS: {
        storeUniqueRecipients(
          recipients.INVESTORS,
          uniqueRecipients,
          uniqueEmails
        );
        setTo(uniqueRecipients);
        break;
      }
      // Directors case
      case presetRecipients.DIRECTORS: {
        storeUniqueRecipients(
          recipients.DIRECTORS,
          uniqueRecipients,
          uniqueEmails
        );
        setTo(uniqueRecipients);
        break;
      }
      // All case
      case presetRecipients.ALL: {
        // Store unique partners
        storeUniqueRecipients(
          recipients.PARTNERS,
          uniqueRecipients,
          uniqueEmails
        );
        // Store unique beneficiaries
        storeUniqueRecipients(
          recipients.BENEFICIARIES,
          uniqueRecipients,
          uniqueEmails
        );
        // Store unique investors
        storeUniqueRecipients(
          recipients.INVESTORS,
          uniqueRecipients,
          uniqueEmails
        );
        // Store unique directors
        storeUniqueRecipients(
          recipients.DIRECTORS,
          uniqueRecipients,
          uniqueEmails
        );
        storeUniqueRecipients(secondaryEmails, uniqueRecipients, uniqueEmails);
        setTo(uniqueRecipients);
        break;
      }
      // Default case
      default: {
        setTo(recipients[selectedGroup]);
        break;
      }
    }
  };

  const getUniqueListBy = (arr: RecipientEmail[], key: string) => [
    ...new Map(
      arr.map((item) => [item[key as keyof typeof item], item])
    ).values(),
  ];

  const validateEmails = (emailsList: RecipientEmail[]) => {
    const allEmails = [
      ...recipients.PARTNERS,
      ...recipients.BENEFICIARIES,
      ...recipients.INVESTORS,
      ...recipients.DIRECTORS,
      ...secondaryEmails,
    ]
      .filter((elem) => elem?.email)
      .map(({ email }) => email.trim());

    let hasInvalidEmails = false;
    const invalidEmails: string[] = [];

    emailsList.forEach(({ email }) => {
      if (!allEmails.includes(email)) {
        hasInvalidEmails = true;
        invalidEmails.push(email);
      }
    });

    return { hasInvalidEmails, invalidEmails };
  };

  const getRecipientsData = (list: string | RecipientEmail[]) => {
    if (presetGroup === presetRecipients.CUSTOM) {
      // AXA flow: partner/category selection sets list as RecipientEmail[]
      if (Array.isArray(list)) {
        return getUniqueListBy(list, 'email');
      }
      // Non-AXA (and legacy): user types emails, list is comma-separated string
      const recipientsList = (list as string)
        .split(',')
        .map((recipient) => recipient.trim());

      const uniqueList = [...new Set(recipientsList)];

      return uniqueList.map((recipient) => ({
        email: recipient.trim(),
        name: '',
      }));
    }

    const uniqueList = getUniqueListBy(list as RecipientEmail[], 'email');

    return uniqueList;
  };

  const handleChangeSubject = (event: React.ChangeEvent<HTMLInputElement>) => {
    const subjectText = event.target.value;
    setSubject(subjectText);
  };

  const handleChangeBody = (content: string) => {
    setBody(content);
  };

  async function getOtherUsers(users: string[]) {
    if (!users.length) {
      setSecondaryEmails([]);
    } else {
      try {
        const otherUsersList = (await store.dispatch(
          getUsers({
            usersList: users,
          })
        )) as User[];

        const otherUsersEmails =
          otherUsersList?.map((user) => ({
            email: user.email,
            name: user.name,
          })) || [];

        setSecondaryEmails(otherUsersEmails);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
      }
    }
  }

  const getSocietySecondaryEmails = () => {
    const userIds: string[] = [];

    partners?.forEach(
      (partner: Partner) =>
        partner?.otherUsers?.length > 0 &&
        partner?.otherUsers?.forEach((user) => userIds.push(user))
    );

    getOtherUsers(userIds);
  };

  const validInputs = () => {
    const validBody = isValidEmailBody(body);
    const validSubject = isValidEmailSubject(subject);

    return validBody && validSubject;
  };

  const handleSendEmail = async () => {
    const hasValidInputs = validInputs();

    if (!hasValidInputs && !isPlatformAXA) {
      errorSwal(t('InvalidText'), t('ContactAdministratorMessage'));
      return;
    }

    setIsSending(true);

    try {
      const filteredRecipients = getRecipientsData(to).map((recipient) => {
        const { email, name } = recipient;
        return {
          email: email.trim(),
          name,
        };
      });

      const { hasInvalidEmails } =
        hasDefaultRecipient || isPlatformAXA
          ? { hasInvalidEmails: false }
          : validateEmails(filteredRecipients);

      if (!hasInvalidEmails) {
        const emailData = {
          to: filteredRecipients,
          subject,
          template: 'sttok-general',
          body: body.split('\n'),
          files: file ? [file] : [],
          presetGroup,
          user: user['_id'],
          society: actualSociety['_id'],
          date: new Date(),
          status: 'SENT',
        };

        dispatch(sendCommEmail(emailData));
      } else {
        dispatch(
          addAlert(alertBodyTypes['ERROR_COMMUNICATIONS_EMAIL_DUPLICATED'])
        );
      }
    } catch (error) {
      dispatch(addAlert(alertBodyTypes['ERROR_COMMUNICATIONS_EMAIL']));
    } finally {
      setIsSending(false);
    }
  };

  const handlePartnerChange = (option: CustomAsyncSelectOption | null) => {
    setCustomPartnerOption(option);
    setCustomCategoryOption(null);
    setPresetGroup(presetRecipients.CUSTOM);
    if (option?.email) {
      setTo([
        {
          name: option.name ?? '',
          email: String(option.email),
        },
      ]);
    } else {
      setTo([]);
    }
  };

  const handleCategoryChange = (option: CustomAsyncSelectOption | null) => {
    setCustomCategoryOption(option);
    if (!option) {
      setTo([]);
      return;
    }
    setCustomPartnerOption(null);
    setPresetGroup(presetRecipients.CUSTOM);
    const categoryId = option.value;
    const partnersInCategory = (partners ?? []).filter((partner: Partner) =>
      partner?.categories?.includes(categoryId)
    );
    const uniqueEmails: string[] = [];
    const recipientList: RecipientEmail[] = [];
    partnersInCategory.forEach((partner: Partner) => {
      const email = partner?.email?.trim();
      if (email && !uniqueEmails.includes(email)) {
        uniqueEmails.push(email);
        recipientList.push({
          name: partner?.name ?? '',
          email,
        });
      }
    });
    setTo(recipientList);
  };

  useEffect(() => {
    if (actualSociety) {
      setRecipients({
        PARTNERS: getSocietyEmails(
          actualSociety,
          partners,
          presetRecipients.PARTNERS
        ),
        BENEFICIARIES: getSocietyEmails(
          actualSociety,
          partners,
          presetRecipients.BENEFICIARIES
        ),
        INVESTORS: getSocietyEmails(
          actualSociety,
          partners,
          presetRecipients.INVESTORS
        ),
        DIRECTORS: getSocietyEmails(
          actualSociety,
          partners,
          presetRecipients.DIRECTORS
        ),
      });
    }
    getSocietySecondaryEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualSociety, partners]);

  return (
    <div className="send-email-modal-container">
      <SendEmailHeader />

      <Modal.Body className="no-padding">
        <div className="nk-block nk-block-lg">
          {!hasDefaultRecipient && !isPlatformAXA && (
            <SendEmailRecipientsMenu
              recipients={recipients}
              onSetRecipients={handleSetRecipients}
            />
          )}

          {isPlatformAXA && (
            <SendEmailCustomRecipientsMenu
              selectedPartnerOption={customPartnerOption}
              selectedCategoryOption={customCategoryOption}
              onPartnerChange={handlePartnerChange}
              onCategoryChange={handleCategoryChange}
            />
          )}

          <div className="card-content p-0 border-top">
            <SendEmailAddresses
              to={to}
              presetGroup={presetGroup}
              onChangeTo={(event) => setTo(event.target.value)}
            />

            <div className="nk-reply-form-editor">
              <SendEmailSubject
                subject={subject}
                onChangeSubject={handleChangeSubject}
              />
              <SendEmailBody body={body} onChangeBody={handleChangeBody} />
            </div>

            <SendEmailFooter
              subject={subject}
              body={body}
              hasRecipients={to?.length > 0}
              file={file}
              setFile={setFile}
              onSendEmail={handleSendEmail}
            />
          </div>
        </div>
      </Modal.Body>

      {isSending && <CustomLoading />}
    </div>
  );
};

export default SendEmail;
